import { inject, Injectable } from '@angular/core';
import {
    addStatusToNotificationEntry,
    AppNotificationService,
    AppNotificationType,
    cancelEscalationTimer,
    cancellationConfirmationRemoved,
    cleanupCompletedPreviously,
    cleanupCompletedPreviouslySucceeded,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigInstanceToken,
    ConfigInterface,
    confirmationCompletedPreviously,
    ConfirmationIdentityState,
    confirmationOpened,
    confirmationSideEffectsFailed,
    confirmationSideEffectsSettled,
    ConfirmationModel,
    ConfirmationSignatureSnapshotState,
    ConfirmationState,
    CseStockService,
    dispatchRecipientsWrite,
    getUuidV4,
    isDefined,
    isPaceFormsEnabledOf,
    JsonMaterialModel,
    mainEquipmentOf,
    markNotificationCompleted,
    MaterialModel,
    MaterialService,
    nextSignLaterStatus,
    NOTIFICATION_STATUS_CODE,
    NotificationIdState,
    NotificationState,
    NotificationStateUtilities,
    NotificationUpdateService,
    removeConfirmation,
    removeRecipientsRequested,
    selectAddresses,
    selectCurrentTimesheet,
    selectDisablePaceForms,
    selectEffectiveStatusById,
    selectNotificationCompletedById,
    selectNotificationEntryById,
    selectUserCountry,
    signLater,
    statusRecordFor,
    submitConfirmationAborted,
    submitConfirmationFailed,
    submitConfirmationRequested,
    submitConfirmationSucceeded,
    TimeRecordingService,
    updateNotificationStatusFields,
    updateStartOfWork,
    upsertConfirmation,
    upsertRecipientsRequested,
} from '@evorait/pace-core';
import { cleanupFormList, cleanupSpqDocs, FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { cleanupSafetyAssessmentForm } from 'libs/pace-forms/src/lib/store/actions/safety-assessment.action';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { PCHAlertService } from '../../../core/services/pch-alerts/pch-alerts.service';
import { LocalStorageSalesQuoteService } from '../../../material/services/local-storage-sales-quote.service';
import {
    ConfirmationRehydrationContext,
    confirmationStateToModel,
    confirmationStateToOutModel,
    notificationIdFromState,
} from '../../services/confirmation-model.converters';
import {
    isOfflineSaveError,
    materialConsumedDate,
    safetyAssessmentFormIdentifier,
    spqMaterialsFor,
} from '../../services/confirmation-submit.payloads';
import { OdataConfirmationService } from '../../services/odata-confirmation.service';

/**
 * Owns the confirmation submit pipeline and the small collection of side-effecting
 * "commands" the facade only dispatches: `saveConfirmationMaterials$`, `signLater$`,
 * `cleanupCompletedPreviously$`, and
 * `confirmationOpened$`. `submitConfirmation$` mirrors the legacy `ConfirmationService.submitConfirmation`
 * + `postConfirmationSubmitCleanup` pipeline as **one** effect, in the exact same collaborator
 * call order, via private helpers named after the legacy privates one-for-one
 * (`saveMileageInformation`, `saveMaterials`, `postSubmitCleanup`).
 * This is the highest-risk flow in the migration — see `confirmation-submit.effects.equivalence.spec.ts`
 * for the collaborator-call-sequence assertion that guards against a silent reordering.
 *
 * Works on plain state, with one exception: the materials. The slice holds no material data, and
 * `MaterialService` and `CseStockService` still keep them as models, so the confirmation is rebuilt into a
 * legacy `ConfirmationModel` (through the converters) for the material writes alone — until the materials
 * get their own slice. The materials themselves never come from the rebuild: a save and a submit both carry
 * them on the action as JSON.
 */
@Injectable()
export class ConfirmationSubmitEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly log = inject<LogInterface>(LogInstanceToken);
    private readonly configService = inject<ConfigInterface>(ConfigInstanceToken);
    private readonly clientManager = inject<ClientManagerInterface>(ClientManagerInstanceToken);
    private readonly notificationUpdateService = inject(NotificationUpdateService);
    private readonly localStorageSalesQuoteService = inject(LocalStorageSalesQuoteService);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly translate = inject(TranslateService);
    private readonly pchAlertsService = inject(PCHAlertService);
    private readonly odataConfirmationService = inject(OdataConfirmationService);
    private readonly formsService = inject(FormsService);
    private readonly materialService = inject(MaterialService);
    private readonly timeRecordingService = inject(TimeRecordingService);
    private readonly cseStockService = inject(CseStockService);

    /**
     * The notification an action is about, read from the slice.
     *
     * A parameterized selector built per read rather than held as a field: the id is only known when
     * the action arrives, and each of these runs once per submit — not per row or per emission.
     *
     * @param id the notification to look up
     * @returns its entry, or `undefined` when the collection does not hold it
     */
    private notificationEntry(id: NotificationIdState): NotificationState | undefined {
        return this.store.selectSignal(selectNotificationEntryById(id))();
    }

    /**
     * @param id the notification to look up
     * @returns the status to treat as current for it
     */
    private effectiveStatus(id: NotificationIdState): NOTIFICATION_STATUS_CODE | undefined {
        return this.store.selectSignal(selectEffectiveStatusById(id))()?.statusCode;
    }

    /**
     * The entire submit pipeline, byte-for-byte in the legacy method's order: persist the
     * incoming snapshot, save mileage, submit to OData, update the notification status to CRM,
     * run the post-submit cleanup, then verify the status stuck.
     */
    public submitConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitConfirmationRequested),
            mergeMap(({ confirmation, signatureSnapshot, materials, correlationId }) => {
                // Every outcome action echoes which confirmation it was about, the same
                // notification/type pair the slice identifies confirmations by.
                const identity: ConfirmationIdentityState = {
                    notificationId: confirmation.notificationId,
                    confirmationType: confirmation.confirmationType,
                };
                // Mirrors the legacy `filterConfirmationStatusComplete()` that gated the whole pipeline:
                // a notification already in the recently-completed list (e.g. completed on another
                // device and synced in) must never be submitted to SAP twice.
                // Mirrors the legacy `filterConfirmationStatusComplete()` that gated the whole pipeline:
                // a notification already recorded as completed (e.g. completed on another device and
                // synced in) must never be submitted to SAP twice. The record lives on the notification
                // entry, so this is a store read rather than a storage round trip.
                if (this.store.selectSignal(selectNotificationCompletedById(identity.notificationId))()) {
                    this.log.warn(
                        `ConfirmationSubmitEffects: submitConfirmation$: confirmation completed previously, not submitting`,
                        NotificationStateUtilities.notificationIdToString(identity.notificationId),
                    );
                    this.store.dispatch(confirmationCompletedPreviously());
                    return of(submitConfirmationAborted({ ...identity, correlationId }));
                }
                return this.store.select(selectCurrentTimesheet).pipe(
                    filter(isDefined),
                    take(1),
                    withLatestFrom(this.store.select(selectAddresses), this.store.select(selectUserCountry)),
                    mergeMap(([timesheet, addresses, userCountry]) =>
                        this.runSubmitPipeline(confirmation, signatureSnapshot, materials, {
                            timesheet,
                            addresses,
                            userCountry,
                        }),
                    ),
                    map(() => submitConfirmationSucceeded({ ...identity, correlationId })),
                    catchError((error) =>
                        of(submitConfirmationFailed({ ...identity, correlationId, error: String(error) })),
                    ),
                );
            }),
        ),
    );

    /**
     * Defer signing: move the notification's status forward. Fire-and-forget, matching the
     * legacy `signLater`'s `void` return and internal subscription.
     */
    public signLater$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(signLater),
                tap(({ notificationId }) => {
                    const currentStatus = this.effectiveStatus(notificationId);
                    if (!isDefined(currentStatus)) {
                        this.log.error(
                            `ConfirmationSubmitEffects: signLater$: could not find notification for '${NotificationStateUtilities.notificationIdToString(notificationId)}'`,
                        );
                        return;
                    }
                    // Both writes are dispatches now, so the error branch the legacy code had here is
                    // gone with the observable it guarded: a dispatch cannot fail, and reporting a
                    // failure to persist the slice belongs to whoever persists it.
                    this.store.dispatch(
                        addStatusToNotificationEntry({
                            id: notificationId,
                            status: statusRecordFor(nextSignLaterStatus(currentStatus), moment().toISOString()),
                        }),
                    );
                    this.store.dispatch(updateNotificationStatusFields({ id: notificationId, changes: {} }));
                }),
            ),
        { dispatch: false },
    );

    /**
     * Reconcile a confirmation completed on another device: save its CRM status, and report success.
     * Dropping the local confirmation is the reducer's, on that success — so a notification that can no
     * longer be found (the legacy early-return) reports success without any status write, and a failed
     * write leaves the confirmation in place instead of removing it anyway.
     */
    public cleanupCompletedPreviously$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cleanupCompletedPreviously),
            mergeMap(({ notificationId, confirmationType, correlationId }) => {
                const identity = { notificationId, confirmationType };
                if (!isDefined(this.notificationEntry(notificationId))) {
                    return of(cleanupCompletedPreviouslySucceeded({ ...identity, correlationId }));
                }
                this.store.dispatch(
                    addStatusToNotificationEntry({
                        id: notificationId,
                        status: statusRecordFor(NOTIFICATION_STATUS_CODE.CRM, moment().toISOString()),
                    }),
                );
                return of(cleanupCompletedPreviouslySucceeded({ ...identity, correlationId }));
            }),
        ),
    );

    /**
     * A confirmation screen was opened, which has to start the working day if nothing else has.
     *
     * The legacy `getConfirmation` ran a second side effect inline, marking the notification's
     * confirmation as started in localStorage. Nothing reads that any more: its only consumer asked
     * whether work was in flight on a cancelled notification, and a notification always is in
     * progress by the time a confirmation exists for it, which that consumer already checks.
     *
     * Fire-and-forget, matching the legacy method's own `.subscribe()`: the timesheet slice reduces the
     * start of work and persists it, so nothing here waits for the write.
     */
    public confirmationOpened$ = createEffect(() =>
        this.actions$.pipe(
            ofType(confirmationOpened),
            mergeMap(() =>
                this.store.select(selectCurrentTimesheet).pipe(
                    filter(isDefined),
                    take(1),
                    filter((timesheet) => !moment(timesheet.startOfWork).isValid()),
                    map(() =>
                        updateStartOfWork({
                            startOfWork: moment().startOf('minute').toJSON(),
                            correlationId: getUuidV4(),
                        }),
                    ),
                ),
            ),
        ),
    );

    /**
     * The writes a save owes beyond storing the confirmation itself, in the order the legacy
     * `ConfirmationService.saveConfirmation` chained them after the storage write: the materials
     * (`confirmation.service.ts:160`), then the service report's recipients (`:166`).
     *
     * The confirmation slice cannot carry either. It stores no material data at all — `materials` is its
     * accordion flag and nothing else — so persisting the slice leaves the materials untouched; and the
     * recipients live in their own slice, which only a recipients action writes. Without this effect both
     * writes happened only at submit, which meant a technician's quantity and consumption-code edits
     * reached storage late and Material History kept showing the status and activity date from before the
     * confirmation was worked on (the collaborator stamps both — see
     * `MaterialService.saveConfirmationMaterialsToLocalStorage`).
     *
     * The materials written are the ones the action carries, as the technician left them — not the rebuilt
     * confirmation's, which `MaterialService` fills from its own list. That list stops holding the instances
     * bound to the UI once a sync replaces them with copies from storage, so the rebuild would write the
     * pre-edit values back (`material.service.spec.ts`, "confirmation save after a sync replaced the
     * materials"). The rebuild is still needed for the time recording the consumption date comes from.
     *
     * The third legacy step, `saveTimesheetNotification` (`:184`), is deliberately absent: on the NgRx
     * implementation it throws, because the confirmation's work entries are no longer pushed into the
     * timesheet at all. They are derived from the confirmation slice on read by
     * `selectInProgressWorkEntries`, gated on the same `getAllowNotificationPause()` switch, and committed
     * for real only when the timesheet slice reduces `submitConfirmationRequested`.
     *
     * Reports its outcome rather than running fire-and-forget: `saveConfirmation` completes only once
     * both these writes and the slice's own have landed, so every path out of here — including the one
     * where there is nothing to do — must emit exactly one settle action for the save's `correlationId`,
     * or the caller waits forever.
     *
     * `defer` keeps the synchronous throws — the rebuild's, and `saveMaterials`' for an unknown notification —
     * inside the stream, where the `catchError` below turns them into a reported failure instead of killing
     * the effect.
     */
    public saveConfirmationSideEffects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(upsertConfirmation),
            // The unmerged timesheet, as `submitConfirmation$` uses: the entries the materials write reads
            // come from the stored confirmation, not from the timesheet projection.
            withLatestFrom(
                this.store.select(selectCurrentTimesheet),
                this.store.select(selectAddresses),
                this.store.select(selectUserCountry),
            ),
            mergeMap(([{ confirmation, materials, correlationId }, timesheet, addresses, userCountry]) => {
                if (!isDefined(timesheet)) {
                    // Nothing to write against, but the save still has to be told the pass is over.
                    return of(confirmationSideEffectsSettled({ correlationId }));
                }
                return defer(() =>
                    this.saveMaterials(
                        this.legacyConfirmation(confirmation, { timesheet, addresses, userCountry }),
                        this.materialService.materialsFromJson(materials ?? []),
                    ),
                ).pipe(
                    mergeMap(() => this.saveRecipients(confirmation)),
                    map(() => confirmationSideEffectsSettled({ correlationId })),
                    catchError((error) => {
                        const message = JSON.stringify(error, Object.getOwnPropertyNames(error));
                        this.log.error(`ConfirmationSubmitEffects: saveConfirmationSideEffects$: ${message}`);
                        return of(confirmationSideEffectsFailed({ correlationId, error: message }));
                    }),
                );
            }),
        ),
    );

    /** Reports each write for which the legacy service used to log at `debug` level. */
    public logWrites$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(submitConfirmationRequested),
                tap(({ confirmation }) => {
                    const { frontId, qmNum, maNum } = confirmation.notificationId;
                    this.log.debug(`ConfirmationSubmitEffects: submitConfirmation$: start submission`, {
                        confirmation: `${frontId}:${qmNum}:${maNum}`,
                    });
                }),
            ),
        { dispatch: false },
    );

    /**
     * Runs the rest of the legacy `submitConfirmation` pipeline against the submitted snapshot, in the
     * exact same order: OData submit (with the same error-notification mapping), CRM status save,
     * post-submit cleanup, then a final status check that throws if the CRM status did not stick.
     *
     * @param confirmation the submitted snapshot
     * @param signatureSnapshot what was signed on the summary, which the slice never stores
     * @param jsonMaterials the submitted materials, which the slice never stores either
     * @param context what rebuilding the confirmation's section payloads needs
     */
    private runSubmitPipeline(
        confirmation: ConfirmationState,
        signatureSnapshot: ConfirmationSignatureSnapshotState | undefined,
        jsonMaterials: readonly JsonMaterialModel[] | undefined,
        context: ConfirmationRehydrationContext,
    ): Observable<void> {
        const id = confirmation.notificationId;
        const notification = this.notificationEntry(id);
        if (!notification) {
            return throwError(
                () =>
                    new Error(
                        `Could not find notification for identifier '${NotificationStateUtilities.notificationIdToString(id)}'`,
                    ),
            );
        }
        // All are built before anything is sent, so a confirmation that cannot be rebuilt fails the submit
        // instead of leaving it half done. The payload and the local write share one material list, as the
        // legacy pipeline shared the UI's — and it is the carried one, for the reason given on
        // `saveConfirmationSideEffects$`.
        const materials = isDefined(jsonMaterials) ? this.materialService.materialsFromJson(jsonMaterials) : undefined;
        const outModel = confirmationStateToOutModel(confirmation, signatureSnapshot, materials, context);
        const legacyConfirmation = this.legacyConfirmation(confirmation, context);
        const now = moment();
        return this.odataConfirmationService.saveConfirmationOutModel(outModel, id).pipe(
            catchError((error) => {
                this.log.error(
                    `ConfirmationSubmitEffects: submitConfirmation$: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                // The offline store refusing the save is not a lost submit — the technician is told the
                // work is queued instead.
                const offline = isOfflineSaveError(error);
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>(
                        this.translate.instant(
                            offline ? 'OFFLINE_STORE_ERROR_TITLE' : 'CONFIRMATION_SUMMARY_SUBMIT_ERROR_TITLE',
                        )
                    ),
                    <string>(
                        this.translate.instant(
                            offline ? 'OFFLINE_STORE_ERROR_BODY' : 'CONFIRMATION_SUMMARY_SUBMIT_ERROR_BODY',
                        )
                    ),
                );
                return throwError(() => new Error(error));
            }),
            tap(() =>
                this.store.dispatch(
                    addStatusToNotificationEntry({
                        id,
                        status: statusRecordFor(NOTIFICATION_STATUS_CODE.CRM, now.toISOString()),
                    }),
                ),
            ),
            // cleanup runs before the status check so that the confirmation is always removed even when
            // saveStatus did not persist the expected CRM status
            mergeMap(() => this.postSubmitCleanup(confirmation, legacyConfirmation, notification, materials ?? [])),
            tap(() => {
                // Re-read rather than trust the dispatch: the point of this check is that the status
                // actually stuck, which is what the legacy code verified too.
                const status = this.effectiveStatus(id);
                if (status !== NOTIFICATION_STATUS_CODE.CRM) {
                    throw new Error(
                        `ConfirmationSubmitEffects: submitConfirmation$: saveStatus: Notification status should be ${NOTIFICATION_STATUS_CODE.CRM} instead of ${status}`,
                    );
                }
            }),
        );
    }

    /**
     * Mirrors the recipients write the legacy `saveConfirmation` chained after the materials
     * (`confirmation.service.ts:161-172`), under the same data-maintenance switch and the same guard on
     * the confirmation actually having a service report.
     *
     * Writes the stored recipients straight to the recipients slice: with data maintenance on — the only
     * case this writes — the legacy model restored them one to one, so there is nothing a rebuild would add.
     * `dispatchRecipientsWrite` is cold — it dispatches on subscribe — so this must stay chained into the
     * stream rather than merely called.
     *
     * @param confirmation the confirmation whose service report holds the recipients
     */
    private saveRecipients(confirmation: ConfirmationState): Observable<void> {
        if (!this.configService.getShowDataMaintenanceEnhancement() || !isDefined(confirmation.serviceReport)) {
            return of(undefined);
        }
        const correlationId = getUuidV4();
        return dispatchRecipientsWrite(
            this.store,
            this.actions$,
            upsertRecipientsRequested({
                notificationId: confirmation.notificationId,
                recipients: [...confirmation.serviceReport.existingRecipients],
                correlationId,
            }),
            correlationId,
        ).pipe(map(() => undefined));
    }

    /**
     * Rebuilds the legacy model the material collaborators still demand.
     *
     * @param confirmation the confirmation to rebuild
     * @param context what the rebuild needs besides the confirmation
     */
    private legacyConfirmation(
        confirmation: ConfirmationState,
        context: ConfirmationRehydrationContext,
    ): ConfirmationModel {
        return confirmationStateToModel(confirmation, context.timesheet, context.addresses, context.userCountry);
    }

    /**
     * Mirrors the legacy private `saveMaterials`, with the materials passed apart from the confirmation.
     *
     * @param confirmation the confirmation whose notification and time recording the write is stamped from
     * @param materials the materials to write
     */
    private saveMaterials(confirmation: ConfirmationModel, materials: MaterialModel[]): Observable<void> {
        if (materials.length === 0) {
            return of(undefined);
        }
        const id = NotificationStateUtilities.toState(confirmation.notificationIdentifier);
        if (!isDefined(this.notificationEntry(id))) {
            throw new Error(
                `Unable to load notification for confirmation of notification '${confirmation.notificationIdentifier.toString()}'`,
            );
        }
        const isConfirmationSubmitted = this.effectiveStatus(id) === NOTIFICATION_STATUS_CODE.CRM;
        const osrEntries = this.timeRecordingService.getOSREntries(
            confirmation.timeRecording.timeRecordingEntries.value,
        );
        const lastOsrEntry =
            osrEntries.length > 0 ? this.timeRecordingService.getLastTimeRecordingEntry(osrEntries) : undefined;
        return this.materialService
            .saveConfirmationMaterialsToLocalStorage(
                materials,
                isConfirmationSubmitted,
                materialConsumedDate(lastOsrEntry),
            )
            .pipe(map(() => undefined));
    }

    /** Mirrors the legacy private `postConfirmationSubmitCleanup`, renamed `postSubmitCleanup` per the migration plan. */
    private postSubmitCleanup(
        confirmation: ConfirmationState,
        legacyConfirmation: ConfirmationModel,
        notification: NotificationState,
        materials: MaterialModel[],
    ): Observable<void> {
        const run = getUuidV4();
        const id = notification.notificationId;
        const mainEquipment = mainEquipmentOf(notification);
        if (!isDefined(mainEquipment)) {
            throw new Error(
                `ConfirmationSubmitEffects: postSubmitCleanup: Notification '${NotificationStateUtilities.notificationIdToString(id)}' has no main equipment!`,
            );
        }
        this.log.debug(`ConfirmationSubmitEffects: postSubmitCleanup`, {
            notificationId: id,
            run,
        });

        this.store.dispatch(
            removeConfirmation({
                notificationId: confirmation.notificationId,
                confirmationType: confirmation.confirmationType,
                correlationId: getUuidV4(),
            }),
        );

        // The status this records is the one the pipeline just wrote; the legacy call read it back off
        // the model, which `saveStatus` had mutated on its way through.
        this.store.dispatch(
            markNotificationCompleted({
                id,
                completion: { status: NOTIFICATION_STATUS_CODE.CRM, timestampCompleted: Date.now() },
            }),
        );

        return this.saveMaterials(legacyConfirmation, materials).pipe(
            tap(() =>
                this.store.dispatch(removeRecipientsRequested({ notificationId: id, correlationId: getUuidV4() })),
            ),
            mergeMap(async () => {
                this.log.debug(
                    `ConfirmationSubmitEffects: postSubmitCleanup: removeEscalationTimer, removeNotificationUpdates, removeCancellationConfirmationForInprogress, removeSalesQuoteFromLocalStorageAsync, update bootstock`,
                    { run },
                );
                this.store.dispatch(cancelEscalationTimer({ notificationId: id }));
                this.notificationUpdateService.removeNotificationUpdates(id);
                this.store.dispatch(cancellationConfirmationRemoved());
                if (this.clientManager.isNgmP58()) {
                    await this.localStorageSalesQuoteService
                        .removeSalesQuoteFromLocalStorageAsync(id)
                        .catch((e) =>
                            this.log.error(
                                'ConfirmationSubmitEffects',
                                'failed to remove sales quote from local storage',
                                JSON.stringify(e),
                            ),
                        );
                    this.cseStockService.setLocalBootStockMaterialModels(
                        this.materialService.getAllBootStockMaterialsForNotification(notificationIdFromState(id)),
                    );
                }
            }),
            mergeMap(() => {
                this.log.debug(`ConfirmationSubmitEffects: postSubmitCleanup: cleanup forms`, { run });
                const paceFormsEnabled = isPaceFormsEnabledOf(
                    notification,
                    this.store.selectSignal(selectDisablePaceForms)(),
                );
                if (this.clientManager.isNgmP58() || paceFormsEnabled) {
                    return this.formsService
                        .getFormList(mainEquipment.materialNumber, mainEquipment.equipmentNumber, id, true)
                        .pipe(
                            take(1),
                            filter(isDefined),
                            map((formList) => {
                                this.store.dispatch(cleanupFormList({ formList }));
                                return undefined;
                            }),
                            catchError(() => {
                                this.log.debug(
                                    `ConfirmationSubmitEffects: postSubmitCleanup: Form list cleanup not necessary since there is no list`,
                                );
                                return of(undefined);
                            }),
                            finalize(() => {
                                if (!this.configService.getDisplayServicePartQuestionnaire()) {
                                    return;
                                }
                                const spqMaterials = spqMaterialsFor(legacyConfirmation, mainEquipment.equipmentNumber);
                                if (spqMaterials.length > 0) {
                                    this.store.dispatch(cleanupSpqDocs({ spqMaterials }));
                                }
                            }),
                        );
                }
                return of(undefined);
            }),
            mergeMap(() => {
                this.log.debug(`ConfirmationSubmitEffects: postSubmitCleanup: cleanup safety assessment`, {
                    run,
                });
                const formIdentifier = safetyAssessmentFormIdentifier(
                    this.configService.getSafetyAssessmentForm(),
                    mainEquipment.equipmentNumber,
                    mainEquipment.materialNumber,
                );
                return this.formsService.checkSafetyAssessmentFormInCache(formIdentifier, id).pipe(
                    map((hasSAFormInCache) => {
                        if (hasSAFormInCache) {
                            this.store.dispatch(
                                cleanupSafetyAssessmentForm({ form: formIdentifier, notification: id }),
                            );
                        }
                        return undefined;
                    }),
                );
            }),
            tap(() => {
                if (this.configService.getCentricarePCHEnabled()) {
                    this.log.debug(`ConfirmationSubmitEffects: postSubmitCleanup: cleanUpAlertPostCRM`, {
                        run,
                    });
                    this.pchAlertsService.cleanUpAlertPostCRM(id);
                }
            }),
            catchError((error) => {
                this.log.error(
                    `ConfirmationSubmitEffects: postSubmitCleanup: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                return of(undefined);
            }),
        );
    }
}
